import React, { useState } from 'react';
import {
  Buildings, FileText, Envelope, Plugs, Bell, Calculator, Tag, Database,
  MagnifyingGlass, Check, CaretRight, Gear, Shield, Globe, CurrencyEur,
  Receipt, Bank, UserCircle, PaintBrush, CloudArrowUp, Key, ArrowSquareOut, DotsThree
} from '@phosphor-icons/react';

/* -------------------------------------------------------------
   Mockup page: 3 design variants for the Instellingen (Settings).
   Non-functional — purely visual, to let the user pick.
   Route: /instellingen-mockups
   ------------------------------------------------------------- */

const VARIANTS = [
  { id: 'A', label: 'A — Verticale sidebar', sub: 'Shopify/Odoo-stijl' },
  { id: 'B', label: 'B — Card-grid overzicht', sub: 'Stripe/Xero-stijl' },
  { id: 'C', label: 'C — Verfijnde horizontale tabs', sub: 'Billit-refresh' },
  { id: 'D', label: 'D — Minimalistisch mono', sub: 'Linear/Notion-stijl' },
];

export default function InstellingenMockupPage() {
  const [variant, setVariant] = useState('A');
  return (
    <div className="space-y-5" data-testid="instellingen-mockup-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#002FA7] flex items-center gap-2">
            <PaintBrush size={28} weight="bold" /> Instellingen — Design voorbeelden
          </h1>
          <div className="text-xs text-gray-500 mt-1">Kies 1 van de 3 stijlen. Daarna rollen we die uit op de échte Instellingen-pagina.</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {VARIANTS.map(v => (
            <button
              key={v.id}
              onClick={() => setVariant(v.id)}
              className={`px-4 py-2 text-sm font-bold border transition-all ${variant === v.id ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#002FA7]'}`}
              data-testid={`mockup-switch-${v.id}`}
            >
              {v.label}
              <div className={`text-[10px] font-normal mt-0.5 ${variant === v.id ? 'text-white/80' : 'text-gray-400'}`}>{v.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {variant === 'A' && <VariantA />}
      {variant === 'B' && <VariantB />}
      {variant === 'C' && <VariantC />}
      {variant === 'D' && <VariantD />}
    </div>
  );
}

/* ========== VARIANT A — verticale sidebar ========== */
function VariantA() {
  const [active, setActive] = useState('bedrijf');
  const groups = [
    {
      titel: 'Algemeen',
      items: [
        { id: 'bedrijf', label: 'Bedrijfsgegevens', icon: Buildings, hint: 'Naam, BTW, adres' },
        { id: 'bestuur', label: 'Bestuurder & Contact', icon: UserCircle, hint: 'E-mail, telefoon' },
        { id: 'talen', label: 'Talen & Munteenheid', icon: Globe, hint: 'NL/FR/EN/DE • EUR' },
      ],
    },
    {
      titel: 'Boekhouding',
      items: [
        { id: 'documenten', label: 'Documenten & Nummering', icon: FileText, hint: 'Prefixen, boekjaar' },
        { id: 'btw', label: 'BTW & Grootboek', icon: Calculator, hint: 'Standaard 21%' },
        { id: 'cat', label: 'Categorieën → Grootboek', icon: Tag, hint: '98 mappings' },
        { id: 'boekingen', label: 'Boekingsregels', icon: Receipt, hint: 'Automatische labels' },
      ],
    },
    {
      titel: 'Communicatie',
      items: [
        { id: 'email', label: 'E-mail Inbox', icon: Envelope, hint: 'SendGrid Inbound' },
        { id: 'herinner', label: 'Herinneringen', icon: Bell, hint: '3 schema\'s actief' },
        { id: 'brieven', label: 'Brieven & Sjablonen', icon: FileText, hint: 'PDF layouts' },
      ],
    },
    {
      titel: 'Integraties & Systeem',
      items: [
        { id: 'int', label: 'Integraties', icon: Plugs, hint: 'SendGrid • Stripe • Peppol', badge: '3' },
        { id: 'security', label: 'Beveiliging', icon: Shield, hint: 'MFA, API-tokens' },
        { id: 'data', label: 'Databeheer', icon: Database, hint: 'Backup, import, export' },
      ],
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
      {/* Sidebar */}
      <div className="section-card">
        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="field-input pl-8 w-full text-xs" placeholder="Zoek in instellingen..." />
          </div>
        </div>
        <div className="py-2">
          {groups.map(g => (
            <div key={g.titel} className="mb-2">
              <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-400 font-bold">{g.titel}</div>
              {g.items.map(it => {
                const Icon = it.icon;
                const isActive = active === it.id;
                return (
                  <button
                    key={it.id}
                    onClick={() => setActive(it.id)}
                    className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition-all border-l-2 ${isActive ? 'bg-[#F0F4FF] border-[#002FA7]' : 'border-transparent hover:bg-gray-50'}`}
                  >
                    <Icon size={16} weight={isActive ? 'fill' : 'regular'} className={isActive ? 'text-[#002FA7]' : 'text-gray-500'} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-${isActive ? 'bold' : 'medium'} ${isActive ? 'text-[#002FA7]' : 'text-gray-700'}`}>{it.label}</div>
                      <div className="text-[10px] text-gray-400 truncate">{it.hint}</div>
                    </div>
                    {it.badge && <span className="text-[9px] bg-[#059669] text-white px-1.5 py-0.5 font-bold">{it.badge}</span>}
                    {isActive && <CaretRight size={11} weight="bold" className="text-[#002FA7]" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Content preview */}
      <div className="space-y-4">
        <div className="section-card">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">Algemeen</div>
              <div className="text-xl font-bold text-gray-800">Bedrijfsgegevens</div>
              <div className="text-xs text-gray-500 mt-0.5">Basisinformatie die op al je facturen en offertes verschijnt.</div>
            </div>
            <button className="btn btn-primary"><Check size={14} weight="bold" className="inline mr-1" /> Opslaan</button>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { l: 'Ondernemingsnummer', v: 'BE 0803.088.437' },
              { l: 'Maatschappelijke naam', v: 'Emergent Billing BV' },
              { l: 'Commerciële naam', v: 'Emergent' },
              { l: 'IBAN', v: 'BE68 5390 0754 7034' },
              { l: 'Standaard BTW', v: '21%' },
              { l: 'Standaard munteenheid', v: 'EUR' },
            ].map((f, i) => (
              <div key={i}>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">{f.l}</label>
                <input className="field-input w-full" defaultValue={f.v} readOnly />
              </div>
            ))}
          </div>
        </div>

        <div className="section-card">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="text-sm font-bold text-gray-800 flex items-center gap-2"><Bank size={16} weight="bold" className="text-[#002FA7]" /> Facturatieadres</div>
            <button className="text-xs text-[#002FA7] font-bold hover:underline">Bewerken</button>
          </div>
          <div className="px-5 py-3 text-xs text-gray-600">Koninklijkelaan 42, 1000 Brussel — België</div>
        </div>
      </div>
    </div>
  );
}

/* ========== VARIANT B — card grid ========== */
function VariantB() {
  const sections = [
    { id: 'bedrijf', titel: 'Bedrijfsgegevens', desc: 'Naam, BTW-nummer, adres, IBAN', icon: Buildings, color: '#002FA7', status: 'Volledig ingevuld', tag: 'ok' },
    { id: 'doc', titel: 'Documenten & Nummering', desc: 'Prefixen, boekjaar, standaardtekst', icon: FileText, color: '#7C3AED', status: '4 reeksen actief', tag: 'ok' },
    { id: 'btw', titel: 'BTW & Grootboek', desc: 'Standaard BTW, categorieën, auto-mapping', icon: Calculator, color: '#059669', status: '98 categorieën gemapt', tag: 'ok' },
    { id: 'herinner', titel: 'Herinneringen', desc: '3 automatische schema\'s + cron', icon: Bell, color: '#D97706', status: 'Actief sinds 15/01', tag: 'ok' },
    { id: 'email', titel: 'E-mail Inbox', desc: 'SendGrid Inbound Parse webhook', icon: Envelope, color: '#DC2626', status: 'Ontvangst geactiveerd', tag: 'ok' },
    { id: 'int', titel: 'Integraties', desc: 'SendGrid, Stripe, Peppol, AI OCR', icon: Plugs, color: '#0891B2', status: '3 actief, 1 inactief', tag: 'warn' },
    { id: 'sec', titel: 'Beveiliging', desc: 'Wachtwoord, MFA, API-sleutels', icon: Shield, color: '#475569', status: 'MFA aanbevolen', tag: 'warn' },
    { id: 'data', titel: 'Databeheer', desc: 'Backup, import, export, archivering', icon: Database, color: '#BE185D', status: 'Laatste backup: gisteren', tag: 'ok' },
  ];
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button key={s.id} className="section-card text-left hover:shadow-md transition-all group" style={{ borderTop: `3px solid ${s.color}` }}>
              <div className="p-5 flex items-start gap-3">
                <div className="w-11 h-11 flex items-center justify-center shrink-0" style={{ background: `${s.color}15` }}>
                  <Icon size={22} weight="bold" style={{ color: s.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                    {s.titel}
                    <ArrowSquareOut size={11} weight="bold" className="text-gray-300 group-hover:text-[#002FA7] transition-colors" />
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1 leading-snug">{s.desc}</div>
                  <div className="mt-3 flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${s.tag === 'ok' ? 'bg-[#059669]' : 'bg-[#D97706]'}`}></span>
                    <span className="text-[10px] font-medium text-gray-500">{s.status}</span>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="section-card">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm font-bold text-gray-800 flex items-center gap-2"><Gear size={15} weight="bold" className="text-[#002FA7]" /> Snelle acties</div>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {[
            { icon: CloudArrowUp, l: 'Backup nu uitvoeren' },
            { icon: Key, l: 'API-sleutel genereren' },
            { icon: FileText, l: 'PDF-layout previewen' },
            { icon: CurrencyEur, l: 'BTW-tarieven beheren' },
          ].map((a, i) => {
            const I = a.icon;
            return (
              <button key={i} className="p-3 border border-gray-200 hover:border-[#002FA7] hover:bg-[#F0F4FF] transition-all text-left flex items-center gap-2">
                <I size={16} weight="bold" className="text-[#002FA7]" />
                <span className="font-medium text-gray-700">{a.l}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ========== VARIANT C — verfijnde horizontale tabs ========== */
function VariantC() {
  const [tab, setTab] = useState('bedrijf');
  const tabs = [
    { id: 'bedrijf', label: 'Bedrijf', icon: Buildings },
    { id: 'doc', label: 'Documenten', icon: FileText },
    { id: 'btw', label: 'BTW & Boekhouding', icon: Calculator },
    { id: 'email', label: 'E-mail & Herinneringen', icon: Envelope },
    { id: 'int', label: 'Integraties', icon: Plugs, badge: '3' },
    { id: 'sec', label: 'Beveiliging', icon: Shield },
    { id: 'data', label: 'Databeheer', icon: Database },
  ];
  const subtabs = {
    bedrijf: ['Algemeen', 'Adressen', 'Bestuurder', 'Bijlagen', 'E-Invoicing'],
    doc: ['Nummering', 'Standaardteksten', 'PDF-layout', 'Betaling'],
    btw: ['BTW tarieven', 'Categorieën', 'Grootboek', 'Aftrekbaarheid'],
    email: ['Verzenden', 'Inbox', 'Herinneringen', 'Sjablonen'],
    int: ['SendGrid', 'Stripe', 'Peppol', 'AI OCR'],
    sec: ['Account', 'MFA', 'API-sleutels', 'Audit log'],
    data: ['Backup', 'Import', 'Export', 'Archivering'],
  };
  const [sub, setSub] = useState('Algemeen');

  return (
    <div className="space-y-4">
      {/* Hero strip */}
      <div className="section-card" style={{ background: 'linear-gradient(135deg, #002FA7 0%, #1E40AF 100%)' }}>
        <div className="p-5 text-white flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold">Emergent Billing BV</div>
            <div className="text-xl font-bold">Configuratie & Voorkeuren</div>
            <div className="text-xs text-white/80 mt-1">Alles wat je nodig hebt om Emergent aan te passen aan jouw bedrijf.</div>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div><div className="text-white/60 text-[10px] uppercase tracking-wider font-bold">Laatst opgeslagen</div><div className="font-bold">vandaag 11:42</div></div>
            <div className="w-px h-8 bg-white/20"></div>
            <div><div className="text-white/60 text-[10px] uppercase tracking-wider font-bold">Integraties</div><div className="font-bold">3 actief</div></div>
          </div>
        </div>
      </div>

      {/* Pill tabs */}
      <div className="flex gap-1 overflow-x-auto bg-gray-100 p-1">
        {tabs.map(t => {
          const I = t.icon;
          const a = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setSub(subtabs[t.id][0]); }}
              className={`px-4 py-2 inline-flex items-center gap-2 text-xs font-bold transition-all whitespace-nowrap ${a ? 'bg-white text-[#002FA7] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <I size={14} weight="bold" />
              {t.label}
              {t.badge && <span className={`text-[9px] px-1.5 py-0.5 ${a ? 'bg-[#002FA7] text-white' : 'bg-gray-300 text-white'}`}>{t.badge}</span>}
            </button>
          );
        })}
      </div>

      {/* Subtabs */}
      <div className="flex gap-4 border-b border-gray-200 text-xs">
        {subtabs[tab].map(s => (
          <button key={s} onClick={() => setSub(s)} className={`pb-2 px-1 border-b-2 font-bold transition-all ${sub === s ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-400 hover:text-gray-700'}`}>{s}</button>
        ))}
        <div className="ml-auto flex items-center gap-2 pb-2">
          <button className="text-gray-400 hover:text-[#002FA7]"><DotsThree size={16} weight="bold" /></button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="section-card">
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="text-sm font-bold text-gray-800">{sub}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Voorbeeld — in variant C blijven de tabs bovenaan, met subtabs eronder en een contextuele helper rechts.</div>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { l: 'Ondernemingsnummer', v: 'BE 0803.088.437' },
              { l: 'Maatschappelijke naam', v: 'Emergent Billing BV' },
              { l: 'Commerciële naam', v: 'Emergent' },
              { l: 'IBAN', v: 'BE68 5390 0754 7034' },
            ].map((f, i) => (
              <div key={i}>
                <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-bold mb-1">{f.l}</label>
                <input className="field-input w-full" defaultValue={f.v} readOnly />
              </div>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
            <button className="btn btn-sm">Annuleren</button>
            <button className="btn btn-primary btn-sm"><Check size={13} weight="bold" className="inline mr-1" /> Opslaan</button>
          </div>
        </div>
        <aside className="section-card">
          <div className="px-4 py-3 border-b border-gray-100 text-xs font-bold text-gray-700 uppercase tracking-wider">Tips</div>
          <div className="p-4 space-y-3 text-xs text-gray-600">
            <div className="p-3 bg-amber-50 border-l-2 border-amber-400">
              <div className="font-bold text-amber-900 mb-1">BTW-nummer formaat</div>
              Gebruik formaat <code className="bg-white px-1">BE 0XXX.XXX.XXX</code>. Emergent valideert automatisch bij het opslaan.
            </div>
            <div className="p-3 bg-blue-50 border-l-2 border-blue-400">
              <div className="font-bold text-blue-900 mb-1">E-Invoicing klaar?</div>
              Peppol is momenteel <strong>niet geactiveerd</strong>. <a className="underline" href="#">Nu activeren →</a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}


/* ========== VARIANT D — Minimalistisch mono (Linear/Notion-stijl) ========== */
function VariantD() {
  const [active, setActive] = useState('algemeen');
  const [open, setOpen] = useState({ bedrijf: true, boekhouding: true, comm: false, sys: false });
  const tree = [
    {
      id: 'bedrijf', label: 'Bedrijf', count: 4,
      kids: [
        { id: 'algemeen', label: 'Algemeen' },
        { id: 'adressen', label: 'Adressen' },
        { id: 'bestuur', label: 'Bestuurder' },
        { id: 'einv', label: 'E-Invoicing' },
      ],
    },
    {
      id: 'boekhouding', label: 'Boekhouding', count: 4,
      kids: [
        { id: 'nummer', label: 'Documentnummering' },
        { id: 'btw', label: 'BTW' },
        { id: 'cat', label: 'Categorieën' },
        { id: 'boekingen', label: 'Boekingsregels' },
      ],
    },
    {
      id: 'comm', label: 'Communicatie', count: 3,
      kids: [
        { id: 'mail', label: 'E-mail' },
        { id: 'herinner', label: 'Herinneringen' },
        { id: 'sjab', label: 'Sjablonen' },
      ],
    },
    {
      id: 'sys', label: 'Systeem', count: 3,
      kids: [
        { id: 'int', label: 'Integraties' },
        { id: 'sec', label: 'Beveiliging' },
        { id: 'data', label: 'Databeheer' },
      ],
    },
  ];

  const fields = [
    { l: 'Ondernemingsnummer', v: 'BE 0803.088.437', hint: 'Formaat: BE gevolgd door 10 cijfers' },
    { l: 'Maatschappelijke naam', v: 'Emergent Billing BV' },
    { l: 'Commerciële naam', v: 'Emergent' },
    { l: 'IBAN', v: 'BE68 5390 0754 7034' },
    { l: 'BIC', v: 'TRIOBEBB' },
    { l: 'Standaard BTW', v: '21%' },
    { l: 'Munteenheid', v: 'EUR' },
    { l: 'Taal', v: 'Nederlands' },
  ];

  return (
    <div className="bg-white" style={{ border: '1px solid #E5E7EB', minHeight: 640 }}>
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr]">
        {/* Left nav — mono, compact, collapsible */}
        <div className="border-r border-gray-200 bg-[#FAFAFA]">
          <div className="px-3 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-white border border-gray-200">
              <MagnifyingGlass size={12} className="text-gray-400" />
              <input className="text-xs bg-transparent outline-none flex-1" placeholder="Zoeken..." />
              <span className="text-[9px] text-gray-400 border border-gray-300 px-1 rounded">⌘K</span>
            </div>
          </div>
          <div className="py-2">
            {tree.map(group => {
              const isOpen = open[group.id];
              return (
                <div key={group.id} className="mb-0.5">
                  <button
                    onClick={() => setOpen({ ...open, [group.id]: !isOpen })}
                    className="w-full px-3 py-1.5 flex items-center gap-1.5 text-left hover:bg-gray-100"
                  >
                    <CaretRight size={10} weight="bold" className={`text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">{group.label}</span>
                    <span className="ml-auto text-[10px] text-gray-400">{group.count}</span>
                  </button>
                  {isOpen && group.kids.map(k => {
                    const a = active === k.id;
                    return (
                      <button
                        key={k.id}
                        onClick={() => setActive(k.id)}
                        className={`w-full pl-8 pr-3 py-1.5 text-left text-xs transition-all ${a ? 'bg-gray-900 text-white font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
                      >
                        {k.label}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right content — breadcrumb + inline-style form */}
        <div>
          {/* Breadcrumb & actions */}
          <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span>Instellingen</span>
              <CaretRight size={10} weight="bold" className="text-gray-300" />
              <span>Bedrijf</span>
              <CaretRight size={10} weight="bold" className="text-gray-300" />
              <span className="text-gray-900 font-medium">Algemeen</span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1 text-gray-400">
                <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                Gesynchroniseerd
              </span>
              <button className="px-3 py-1 border border-gray-900 bg-gray-900 text-white text-xs font-medium hover:bg-gray-800">
                Opslaan
              </button>
            </div>
          </div>

          {/* Page header */}
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-xl font-bold text-gray-900 tracking-tight">Algemeen</h2>
            <p className="text-xs text-gray-500 mt-1 max-w-xl leading-relaxed">Basisgegevens van je onderneming. Deze informatie verschijnt op facturen, offertes en wordt gebruikt voor BTW-aangifte en boekhoudexport.</p>
          </div>

          {/* Inline form — Notion-stijl key/value rijen */}
          <div className="border-t border-gray-100">
            {fields.map((f, i) => (
              <div key={i} className="grid grid-cols-[200px_1fr] items-center border-b border-gray-100 px-6 py-2.5 hover:bg-gray-50 transition-colors group">
                <div>
                  <div className="text-xs font-medium text-gray-500">{f.l}</div>
                  {f.hint && <div className="text-[10px] text-gray-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">{f.hint}</div>}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <input
                    className="flex-1 bg-transparent outline-none text-sm text-gray-900 border-0 p-0 focus:bg-white focus:ring-1 focus:ring-gray-900 focus:px-2 focus:py-1 transition-all"
                    defaultValue={f.v}
                    readOnly
                  />
                  <span className="text-[10px] text-gray-300 opacity-0 group-hover:opacity-100">↵</span>
                </div>
              </div>
            ))}
          </div>

          {/* Metadata footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-5 text-[11px] text-gray-400">
            <div>Laatst bewerkt: <span className="text-gray-600 font-medium">vandaag om 11:42</span></div>
            <div>Door: <span className="text-gray-600 font-medium">jij@bedrijf.be</span></div>
            <div className="ml-auto flex items-center gap-3">
              <a href="#" className="hover:text-gray-900">Geschiedenis</a>
              <a href="#" className="hover:text-gray-900">Dupliceren</a>
              <a href="#" className="hover:text-red-600">Reset</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
